import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UUID, UUID_NULL } from '@okcargo-apps/shared-domain';
import {
  ECapabilities,
  EMailingPeriodicity,
  getEmptyIAccount,
  getEmptyIAccountPreferences,
  IAccount,
  IAccountPreferences,
  ICredentials,
  IPreferenceRoutes,
} from '@okcargo-apps/shared-domain-ionic';
import { JwtService } from '@okcargo-apps/shared-infrastructure';
import {
  AccountCommandService,
  AccountQueryService,
  CountryStateService,
} from '@okcargo-apps/shared-infrastructure-ionic';
import {
  CreateOrUpdateCollaboratorRoutesPreferencesCommand,
  UpdateCollaboratorQuestionairCommand,
  UpdateContactMailingPreferencesCommand,
} from '@okcargo/command-processor';
import { enGB, es } from 'date-fns/locale';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { BehaviorSubject, firstValueFrom, from, map, Observable, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly jwtService = inject(JwtService);
  private readonly accountQueryService = inject(AccountQueryService);
  private readonly accountCommandService = inject(AccountCommandService);
  private readonly countryStateService = inject(CountryStateService);
  private readonly translate = inject(TranslateService);
  private readonly dateFnsConfigurationService = inject(DateFnsConfigurationService);

  private readonly _account$: BehaviorSubject<IAccount> = new BehaviorSubject(getEmptyIAccount());
  private readonly _accountPreferences$: BehaviorSubject<IAccountPreferences> = new BehaviorSubject(
    getEmptyIAccountPreferences(),
  );

  private readonly isAccountPreferencesLoaded = signal(false);

  // constructor() {}

  public get account$(): Observable<IAccount> {
    return this._account$.asObservable();
  }

  public get accountPreferences$(): Observable<IAccountPreferences> {
    return from(this.checkAccountPreferences()).pipe(switchMap(() => this._accountPreferences$.asObservable()));
  }

  public get userCapabilities$(): Observable<ECapabilities[]> {
    return this._account$.pipe(map((account) => account.capabilities || []));
  }

  public getCarrierId(): UUID | null {
    const company = this._account$.getValue().company;
    return company?.id || null;
  }

  public hasCapability$(capability: ECapabilities): Observable<boolean> {
    return this._account$.pipe(map((account) => account.capabilities.includes(capability)));
  }

  public async checkUserAuthentication(): Promise<void> {
    try {
      const { account, accountPreferences } = await this.accountQueryService.getIdentity(
        this._account$.getValue(),
        this._accountPreferences$.getValue(),
      );
      this._account$.next(account);
      this._accountPreferences$.next(accountPreferences);

      this.checkAccountPreferences();

      this.setDateFnsLocale(account.lang);
      this.jwtService.setUserDataReceived(true);
    } catch (error) {
      console.warn(error);
    }
  }

  public async login(credentials: ICredentials): Promise<void> {
    // console.log('login() in account service');
    await firstValueFrom(
      this.jwtService.login$(credentials).pipe(
        tap((response) => {
          console.log('account response', response);
          this.jwtService.setAccessToken(response.access_token || response.accessToken);
          this.jwtService.setRefreshToken(response.refresh_token || response.refreshToken);
        }),
      ),
    );
    await this.checkUserAuthentication();
  }

  public async validateToken(token: string): Promise<void> {
    console.log('validateToken() in account service');
    await firstValueFrom(
      this.jwtService.validateToken$(token).pipe(
        tap((response) => {
          this.jwtService.setAccessToken(response.access_token || response.accessToken);
          this.jwtService.setRefreshToken(response.refresh_token || response.refreshToken);
        }),
      ),
    );
    await this.checkUserAuthentication();
  }

  public async logout(): Promise<void> {
    // TODO make logout with send command to server??
    // await firstValueFrom(this.jwtService.logout$());
    this._account$.next(getEmptyIAccount());
    this.jwtService.clearTokens();
  }

  public async changeLang(lang: string): Promise<void> {
    this.translate.setFallbackLang(lang);
    this.translate.use(lang);
    this.setDateFnsLocale(lang);
    this.accountCommandService.saveLang(lang);
  }

  public changePasswordRequest(email: string): Observable<void> {
    return this.accountCommandService.changePasswordRequest(email);
  }

  public changePasswordConfirm(email: string, newPassword: string, key: string): Observable<void> {
    return this.accountCommandService.changePasswordConfirm(email, newPassword, key);
  }

  public changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.accountCommandService.changePassword(currentPassword, newPassword);
  }

  // TODO remove, use in updatePreferences
  // public async updateMailingPreferences(command: UpdateMailingPreferencesCommand): Promise<void> {
  //   try {
  //     await this.accountCommandService.updateMailingPreferences(command as UpdateMailingPreferencesCommand);
  //   } catch (error: any) {
  //     // TODO implement this
  //     console.warn(error);
  //   }
  // }

  // TODO
  public async updatePreferences(preferences: any): Promise<{ saved: boolean }> {
    try {
      // TODO type this and use it
      const commandPreferences = {
        // id: preferences.id,
        carrierId: preferences.carrierId,

        emailContactConsent: preferences.emailContactConsent,
        emailContact: preferences.emailContact,
        phoneContactConsent: preferences.phoneContactConsent,
        phoneContact: preferences.phoneContact,
        whatsappContactConsent: preferences.whatsappContactConsent,
        whatsappContact: preferences.whatsappContact,

        freightsPerWeek: preferences.freightsPerWeek,
        canvasNumber: preferences.canvasNumber,
        boxNumber: preferences.boxNumber,
        frigoNumber: preferences.frigoNumber,
        megaNumber: preferences.megaNumber,

        // TODO Keep only for compatibility with the old questionnaire. In the future, remove
        ...preferences.oldQuestionnaire,
      } as UpdateCollaboratorQuestionairCommand;

      if (preferences.id !== UUID_NULL) {
        commandPreferences.id = preferences.id;
      }

      await this.accountCommandService.updateCollaboratorQuestionair(
        commandPreferences as UpdateCollaboratorQuestionairCommand,
      );

      const commandRoutes = {
        id: crypto.randomUUID() as UUID,
        carrierId: preferences.carrierId,
        collaboratorRoutesPreferences: preferences.routes.map((route: IPreferenceRoutes) => ({
          id: route.id,
          carrierId: preferences.carrierId,
          vehicleType: route.vehicleType,
          originCountryIsoCode: route.originCountryIsoCode,
          originPostalCode: route.originStateCode ?? null,
          destinationCountryIsoCode: route.destinationCountryIsoCode,
          destinationPostalCode: route.destinationStateCode ?? null,
        })),
      };

      await this.accountCommandService.updateCollaboratorRoutesPreferences(
        commandRoutes as CreateOrUpdateCollaboratorRoutesPreferencesCommand,
      );

      const mailingPreferencesCommand = {
        sendMarketEmail: true,
        emailPeriodicity: preferences.emailPeriodicity,
      };
      if (preferences.emailPeriodicity === EMailingPeriodicity.NEVER) {
        mailingPreferencesCommand.sendMarketEmail = false;
        mailingPreferencesCommand.emailPeriodicity = EMailingPeriodicity.HALF_HOURLY;
      }

      await this.accountCommandService.updateContactMailingPreferences(
        mailingPreferencesCommand as UpdateContactMailingPreferencesCommand,
      );

      this.isAccountPreferencesLoaded.set(false);
      setTimeout(() => {
        this.checkAccountPreferences();
      }, 1000);

      return { saved: true };
    } catch (error) {
      console.error('[AccountService] Error updating preferences:', error);
      return { saved: false };
    }
  }

  private async checkAccountPreferences(): Promise<void> {
    const companyId = this._account$.getValue().company?.id;
    const userEmail = this._account$.getValue().email;

    if (companyId) {
      if (this.isAccountPreferencesLoaded()) return;

      // TODO now from company, in the future from account
      const accountPreferences = await this.accountQueryService.findAccountPreferences(
        companyId,
        userEmail,
        this._accountPreferences$.getValue(),
      );

      if (accountPreferences.routes?.length) {
        accountPreferences.routes = await this.addStateNamesToRoutes(accountPreferences.routes);
      }

      this._accountPreferences$.next(accountPreferences);
      this.isAccountPreferencesLoaded.set(true);
    }
    // console.log('questionary query (findAccountPreferences()): ', questionary);
  }

  // TODO move to StateService ?¿?
  public async addStateNamesToRoutes(routes: IPreferenceRoutes[]): Promise<IPreferenceRoutes[]> {
    const enhancedRoutes = await Promise.all(
      routes.map(async (route) => {
        const promises = [];

        if (route.originStateCode) {
          promises.push(
            firstValueFrom(
              this.countryStateService.findStateByCode$(route.originCountryIsoCode, route.originStateCode),
            ),
          );
        } else {
          promises.push(Promise.resolve(null));
        }

        if (route.destinationStateCode) {
          promises.push(
            firstValueFrom(
              this.countryStateService.findStateByCode$(route.destinationCountryIsoCode, route.destinationStateCode),
            ),
          );
        } else {
          promises.push(Promise.resolve(null));
        }

        const [originState, destinationState] = await Promise.all(promises);

        return {
          ...route,
          originStateName: originState?.state,
          destinationStateName: destinationState?.state,
        };
      }),
    );

    return enhancedRoutes;
  }

  private setDateFnsLocale(lang: string): void {
    if (lang === 'es') {
      this.dateFnsConfigurationService.setLocale(es);
    } else {
      this.dateFnsConfigurationService.setLocale(enGB);
    }
  }

  // TODO acceptPrivacyPolicy() {
}
